import { useState } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { contactTopics } from "@/data/mock";
import { toast } from "sonner";
import { SEO, PAGE_SEO } from "@/components/SEO";

const CONTACT_API_URL = "https://api.lifely.guide/api/contact";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Message sent! We'll get back to you soon.");
        setForm({ name: "", email: "", subject: "", message: "" });
      } else {
        toast.error(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Unable to send message. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-24">
      <SEO {...PAGE_SEO.contact} />
      <section className="py-14 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left info */}
            <div>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4"
              >
                Get in touch
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-serif text-4xl md:text-5xl text-foreground"
              >
                Contact
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.6 }}
                className="mt-4 text-lg text-muted-foreground max-w-md leading-relaxed"
              >
                Questions, feedback, or partnership enquiries — we'd love to
                hear from you.
              </motion.p>

              {/* Contact image */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.6 }}
                className="mt-10 rounded-2xl overflow-hidden shadow-lg"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_cc441a7d-dbdb-42e3-be6e-af4a1804c84b/artifacts/0ow5osxi_photo-1556204975-1851fadab092.avif"
                  alt="Message icon"
                  className="w-full h-[240px] object-cover"
                />
              </motion.div>
            </div>

            {/* Right form */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <form
                onSubmit={handleSubmit}
                className="space-y-5 bg-card border border-border/50 rounded-2xl p-8"
              >
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-foreground/80">
                    Name
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                    placeholder="Your name"
                    className="rounded-lg h-11 bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground/80">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    placeholder="your@email.com"
                    className="rounded-lg h-11 bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Subject</Label>
                  <Select
                    value={form.subject}
                    onValueChange={(val) =>
                      setForm({ ...form, subject: val })
                    }
                  >
                    <SelectTrigger className="rounded-lg h-11 bg-background">
                      <SelectValue placeholder="Select a subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {contactTopics.map((topic) => (
                        <SelectItem key={topic} value={topic}>
                          {topic}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-foreground/80">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    placeholder="Tell us what's on your mind..."
                    rows={5}
                    className="rounded-lg resize-none bg-background"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full rounded-full bg-foreground text-background hover:bg-foreground/85 h-12 font-medium"
                >
                  {sending ? "Sending..." : "Send message"}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
